import { WORKFLOW_FIRST_PATH, type AddStepPresetId, type WorkflowStepType, type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { cn, cssVars, Icon, type CssVarMap } from "@sagot/ui";
import { useId, useRef, useState, type PointerEvent } from "react";
import { AddStepMenu } from "../builder/add-step-menu";
import { NoteChip } from "../builder/note-chip";
import { PathActions } from "../builder/path-actions";
import { PathNameInput } from "../builder/path-name-input";
import { ProblemBadge } from "../builder/problem-badge";
import { useBuilderTools } from "../builder/builder-tools";
import { useProblems } from "../builder/problems-context";
import { useRefs } from "../builder/refs";
import { STEP_ICONS } from "../builder/step-icons";
import { summarize } from "../builder/step-summary";
import { exitSlotsOf } from "../exit-slots";
import { problemText } from "../problem-model";
import type { PathDef, Step } from "../types";
import { TRIGGER_TILE, type Box, type Point } from "./canvas-layout";
import { ExitRow } from "./canvas-exit-row";
import { DropChip, dropRing } from "./canvas-drop";
import { useConnectors } from "./connector-context";

const B = "engage.workflows.builder";
const C = "engage.workflows.canvas";
const K = "engage.workflows.connectors";

/** Position and size of a tile as CSS custom properties (the only inline style allowed). */
const boxVars = (box: Box): CssVarMap => ({ "--geo-x": `${String(box.x)}px`, "--geo-y": `${String(box.y)}px`, "--geo-w": `${String(box.width)}px`, "--geo-h": `${String(box.height)}px` });
const TILE_BOX = "absolute left-[var(--geo-x)] top-[var(--geo-y)] h-[var(--geo-h)] w-[var(--geo-w)]";

interface TileMove {
  offset: Point;
  zoom: number;
  onMove: (offset: Point) => void;
}

/** Dragging a tile's header moves the tile (the position is a view setting and never part of the workflow). */
function useTileDrag({ offset, zoom, onMove }: TileMove) {
  const origin = useRef<{ pointer: Point; offset: Point } | null>(null);
  return {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      origin.current = { pointer: { x: event.clientX, y: event.clientY }, offset };
    },
    onPointerMove: (event: PointerEvent<HTMLElement>) => {
      const start = origin.current;
      if (!start) return;
      const next = { x: start.offset.x + (event.clientX - start.pointer.x) / zoom, y: start.offset.y + (event.clientY - start.pointer.y) / zoom };
      if (Number.isFinite(next.x) && Number.isFinite(next.y)) onMove(next);
    },
    onPointerUp: (event: PointerEvent<HTMLElement>) => {
      origin.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    },
  };
}

interface TriggerTileProps extends TileMove {
  trigger: WorkflowTrigger;
  box: Box;
  channels: readonly string[];
  agentCount: number;
  onOpen: () => void;
}

/** The trigger: what starts the workflow, and who it is for; opens the same settings as the list view's trigger card. */
export function TriggerTile({ trigger, box, channels, agentCount, onOpen, ...move }: TriggerTileProps) {
  const { t } = useTranslation();
  const drag = useTileDrag(move);
  const view = useConnectors().dropView(TRIGGER_TILE);
  return (
    <div className={cn(TILE_BOX, "rounded-lg border-2 border-primary bg-surface shadow-sm", view && "opacity-60")} style={cssVars(boxVars(box))} data-testid="canvas-tile-trigger">
      <DropChip view={view} />
      <div className="flex h-full flex-col">
        <div className="flex h-5 cursor-grab touch-none items-center justify-center text-fg-subtle active:cursor-grabbing" aria-hidden="true" {...drag}>
          <Icon name="grip-vertical" className="size-3 rotate-90" />
        </div>
        <button type="button" className="flex min-h-0 flex-1 items-start gap-3 rounded-b-md px-3 pb-3 text-left" onClick={onOpen}>
          <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-primary text-on-primary">
            <Icon name="zap" className="size-4" />
          </span>
          <span className="min-w-0">
            <span className="block text-[11px] font-bold uppercase tracking-wider text-primary-ink">{t(`${B}.trigger`)}</span>
            <span className="block truncate text-sm font-bold">{t(`engage.workflows.triggers.${trigger}`)}</span>
            <span className="block truncate text-xs text-fg-subtle">
              {channels.length > 0 ? channels.map((channel) => t(`engage.channels.${channel}`)).join(", ") : t("engage.workflows.allChannels")}
              {agentCount > 0 ? ` · ${t(`${C}.agents`, { count: agentCount })}` : ""}
            </span>
          </span>
        </button>
      </div>
    </div>
  );
}

interface StepButtonProps {
  step: Step;
  index: number;
  onEdit: (index: number) => void;
}

function StepButton({ step, index, onEdit }: StepButtonProps) {
  const { t } = useTranslation();
  const refs = useRefs();
  const problemsId = useId();
  const label = t(`engage.workflows.steps.${step.type}`);
  const summary = summarize(step, refs, t);
  const problems = useProblems().byStep.get(step.id) ?? [];
  const flagged = problems.length > 0;
  const messages = [...new Set(problems.map((problem) => problemText(problem, t)))].join(" ");
  return (
    <>
      <button
        type="button"
        className={cn("flex h-[var(--geo-step)] w-full items-center gap-2 rounded-md border px-2 text-left hover:bg-primary-soft", flagged ? "border-warning bg-warning-soft" : "border-line bg-sunken")}
        onClick={() => { onEdit(index); }}
        aria-describedby={flagged ? problemsId : undefined}
        title={flagged ? messages : undefined}
        data-testid="canvas-step"
        data-flagged={flagged ? "true" : undefined}
      >
        <Icon name={STEP_ICONS[step.type]} className="size-4 shrink-0 text-primary-ink" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[10px] font-bold uppercase tracking-wider text-fg-subtle">{label}</span>
          <span className="block truncate text-xs">{summary}</span>
        </span>
        {flagged ? <Icon name="alert-triangle" className="size-3.5 shrink-0 text-warning" /> : null}
      </button>
      {flagged ? <span id={problemsId} className="sr-only">{messages}</span> : null}
    </>
  );
}

interface StepExitsProps {
  step: Step;
  pathId: string;
  index: number;
  lastStep: boolean;
}

/** The exits of a step, one row each under it; a step that never leaves its path has none. */
function StepExits({ step, pathId, index, lastStep }: StepExitsProps) {
  const { t } = useTranslation();
  const slots = exitSlotsOf(step);
  if (slots.length === 0) return null;
  const stepName = t(`engage.workflows.steps.${step.type}`);
  return (
    <ul aria-label={t(`${K}.exitsOf`, { step: stepName })}>
      {slots.map((slot) => (
        <ExitRow key={slot.key} source={{ path: pathId, index, key: slot.key }} slot={slot} stepName={stepName} lastStep={lastStep} />
      ))}
    </ul>
  );
}

interface PathTileProps extends TileMove {
  id: string;
  path: PathDef;
  box: Box;
  trigger: WorkflowTrigger;
  disabled: boolean;
  onEdit: (index: number) => void;
  onAdd: (type: WorkflowStepType, preset?: AddStepPresetId) => void;
  onReplace: (type: WorkflowStepType, preset?: AddStepPresetId) => void;
}

/** One path as a tile: header (its name, problems, notes and ⋯ menu), its steps (each opens the step sheet) and, when editable, an "Add step" row. */
export function PathTile({ id, path, box, trigger, disabled, onEdit, onAdd, onReplace, ...move }: PathTileProps) {
  const { t } = useTranslation();
  const drag = useTileDrag(move);
  const api = useConnectors();
  const tools = useBuilderTools();
  const problems = useProblems().byPath.get(id) ?? [];
  const [renaming, setRenaming] = useState(false);
  const title = api.pathTitle(id);
  const view = api.dropView(id);
  const stopRenaming = () => {
    setRenaming(false);
    window.requestAnimationFrame(() => { document.querySelector<HTMLElement>(`[data-tile-heading="${id}"]`)?.focus({ preventScroll: true }); });
  };
  return (
    <section className={cn(TILE_BOX, "flex flex-col rounded-lg bg-surface", dropRing(view, api.highlight === id))} style={cssVars(boxVars(box))} aria-label={title} data-tile={id} data-testid={`canvas-tile-${id}`}>
      <DropChip view={view} />
      <div className="flex h-[var(--geo-header)] shrink-0 items-center gap-1 border-b border-line pl-3 pr-1">
        {renaming ? (
          <>
            <h3 data-tile-heading={id} tabIndex={-1} className="shrink-0 text-xs font-bold">{t(`${B}.path`, { id })}</h3>
            <PathNameInput
              pathTitle={t(`${B}.path`, { id })}
              initial={path.label ?? ""}
              className="h-7 min-w-0 flex-1 text-xs"
              onSave={(name) => { tools?.rename(id, name); stopRenaming(); }}
              onCancel={stopRenaming}
            />
          </>
        ) : (
          <h3
            data-tile-heading={id}
            tabIndex={-1}
            title={id === WORKFLOW_FIRST_PATH ? `${title} · ${t(`${B}.pathFrom`)}` : title}
            className="flex h-full min-w-0 flex-1 cursor-grab touch-none items-center text-xs font-bold active:cursor-grabbing"
            {...drag}
          >
            <span className="truncate">{title}</span>
          </h3>
        )}
        <ProblemBadge problems={problems} />
        <NoteChip pathId={id} pathTitle={title} />
        {disabled ? null : <PathActions id={id} title={title} onRename={() => { setRenaming(true); }} />}
      </div>
      <ol className="flex flex-col gap-[var(--geo-step-gap)] p-[var(--geo-pad)]">
        {path.steps.length === 0 ? (
          <li className="flex h-[var(--geo-step)] items-center justify-center rounded-md border border-dashed border-line text-xs text-fg-subtle">{t(`${B}.noSteps`)}</li>
        ) : (
          path.steps.map((step, index) => (
            <li key={step.id}>
              <StepButton step={step} index={index} onEdit={onEdit} />
              <StepExits step={step} pathId={id} index={index} lastStep={index === path.steps.length - 1} />
            </li>
          ))
        )}
      </ol>
      {disabled ? null : (
        <div className="flex h-[var(--geo-footer)] items-center px-2">
          <AddStepMenu trigger={trigger} pathId={id} path={path} disabled={false} onAdd={onAdd} onReplace={onReplace} />
        </div>
      )}
    </section>
  );
}
